<template>
    <v-dialog
        :value="value"
        @input="$emit('input', $event)"
        max-width="760"
        aria-labelledby="region-dialog-title"
        content-class="region-dialog"
        scrollable
    >
        <v-card class="region-dialog-card">
            <v-card-title class="region-dialog-title">
                <span id="region-dialog-title">Выбор региона</span>
                <v-spacer />
                <v-btn @click="close" icon aria-label="Закрыть выбор региона">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <div class="region-dialog-search">
                <v-text-field
                    v-model="search"
                    label="Страна, регион или населённый пункт"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    hide-details
                    clearable
                    autofocus
                    :disabled="loading || !!error"
                />
                <div class="caption grey--text text--darken-1 mt-3">
                    Можно выбрать страну целиком, регион или отдельные населённые пункты.
                </div>
            </div>
            <v-progress-linear v-if="loading" indeterminate />
            <v-card-text class="region-dialog-list">
                <v-alert v-if="error" type="error" text dense>
                    {{ error }}
                    <v-btn @click="$emit('retry')" :loading="loading" text small>Повторить</v-btn>
                </v-alert>
                <div v-else-if="!loading && !visibleRows.length" class="py-6 text-center grey--text">
                    Регионы и населённые пункты не найдены
                </div>
                <div v-if="!loading && !error" role="group" aria-label="Страны, регионы и населённые пункты">
                    <div
                        v-for="row in visibleRows"
                        :key="row.node.id"
                        class="region-row"
                        :style="{ paddingLeft: row.level * 24 + 'px' }"
                    >
                        <v-btn
                            v-if="row.node.children.length"
                            @click="toggleExpanded(row.node.id)"
                            :aria-expanded="String(row.expanded)"
                            :aria-label="(row.expanded ? 'Свернуть ' : 'Развернуть ') + row.node.name"
                            icon
                            small
                            class="region-expand"
                        >
                            <v-icon color="grey">{{ row.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                        </v-btn>
                        <span v-else class="region-expand-placeholder" />
                        <v-checkbox
                            :input-value="draft.includes(row.node.id)"
                            @change="toggleSelection(row.node.id)"
                            :label="row.node.name"
                            :title="row.node.label"
                            hide-details
                            color="primary"
                            class="region-checkbox"
                        />
                    </div>
                </div>
            </v-card-text>
            <v-card-actions class="region-dialog-actions">
                <v-btn @click="draft = []" :disabled="!draft.length || loading" text color="primary">Сбросить</v-btn>
                <span class="caption grey--text region-selection-count">Выбрано: {{ draft.length }}</span>
                <v-spacer />
                <v-btn @click="close" text color="primary">Отменить</v-btn>
                <v-btn @click="save" :disabled="loading || !!error || !items.length" color="primary" depressed>Выбрать</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'RegionDialog',
    props: {
        value: Boolean,
        items: { type: Array, default: () => [] },
        selection: { type: Array, default: () => [] },
        loading: Boolean,
        error: { type: String, default: '' },
    },
    data () {
        return { draft: [], search: '', expanded: [] }
    },
    computed: {
        indexedItems () {
            return new Map(this.items.map(item => [String(item.id), item]))
        },
        tree () {
            const indexed = new Map(this.items.map(item => [String(item.id), {
                ...item, id: String(item.id), children: [],
            }]))
            const roots = []
            indexed.forEach(node => {
                const parent = node.parent_id == null ? null : indexed.get(String(node.parent_id))
                if (parent) parent.children.push(node)
                else roots.push(node)
            })
            const sort = nodes => {
                nodes.sort((a, b) => a.name.localeCompare(b.name, 'ru'))
                nodes.forEach(node => sort(node.children))
            }
            sort(roots)
            // Keep the most commonly used country at the start of the directory.
            const russia = roots.findIndex(node => node.id === '113')
            if (russia > 0) roots.unshift(...roots.splice(russia, 1))
            return roots
        },
        searchResults () {
            const words = this.normalize(this.search).split(/\s+/).filter(Boolean)
            if (!words.length) return { nodes: this.tree, expanded: [] }
            const expanded = []
            const filter = nodes => nodes.reduce((matches, node) => {
                const text = this.normalize(node.name + ' ' + (node.path || ''))
                if (words.every(word => text.includes(word))) {
                    matches.push(node)
                } else {
                    const children = filter(node.children)
                    if (children.length) {
                        expanded.push(node.id)
                        matches.push({ ...node, children })
                    }
                }
                return matches
            }, [])
            return { nodes: filter(this.tree), expanded }
        },
        visibleRows () {
            const rows = []
            const expanded = new Set(this.expanded)
            const visit = (nodes, level = 0) => {
                nodes.forEach(node => {
                    const open = expanded.has(node.id)
                    rows.push({ node, level, expanded: open })
                    if (open) visit(node.children, level + 1)
                })
            }
            visit(this.searchResults.nodes)
            return rows
        },
    },
    watch: {
        value: {
            immediate: true,
            handler (open) {
                if (open) {
                    this.draft = [...new Set(this.selection.map(String))]
                    this.search = ''
                    this.expanded = this.selectedAncestors()
                }
            },
        },
        search (query) {
            this.expanded = this.normalize(query) ? this.searchResults.expanded : this.selectedAncestors()
        },
        items () {
            if (this.value) {
                this.expanded = this.normalize(this.search) ? this.searchResults.expanded : this.selectedAncestors()
            }
        },
    },
    methods: {
        normalize (value) {
            return (value || '').toLocaleLowerCase('ru').replace(/ё/g, 'е').replace(/\s+/g, ' ').trim()
        },
        selectedAncestors () {
            const expanded = new Set(['113'])
            this.draft.forEach(id => {
                let node = this.indexedItems.get(id)
                while (node && node.parent_id != null && !expanded.has(String(node.parent_id))) {
                    expanded.add(String(node.parent_id))
                    node = this.indexedItems.get(String(node.parent_id))
                }
            })
            return [...expanded]
        },
        toggleExpanded (id) {
            this.expanded = this.expanded.includes(id) ? this.expanded.filter(value => value !== id) : [...this.expanded, id]
        },
        toggleSelection (id) {
            this.draft = this.draft.includes(id) ? this.draft.filter(value => value !== id) : [...this.draft, id]
        },
        close () {
            this.$emit('input', false)
        },
        save () {
            // HH accepts country and region IDs directly; do not expand them into city IDs.
            this.$emit('save', [...this.draft])
            this.close()
        },
    },
}
</script>

<style scoped>
.region-dialog-card {
    border-radius: inherit;
    overflow: hidden;
}
.region-dialog-title {
    padding: 24px 28px 16px;
    font-size: 26px;
    line-height: 1.3;
}
.region-dialog-search {
    flex: none;
    padding: 8px 28px 20px;
}
.region-dialog-list {
    min-height: 0;
    padding: 0 28px 12px;
}
.region-row {
    display: flex;
    align-items: center;
    min-height: 48px;
}
.region-expand, .region-expand-placeholder {
    flex: none;
    width: 28px;
    margin-right: 8px;
}
.region-checkbox {
    flex: 1;
    min-width: 0;
    margin: 0;
    padding: 0;
}
.region-checkbox ::v-deep .v-label {
    color: #202124;
    line-height: 1.5;
}
.region-dialog-actions {
    padding: 20px 28px 24px;
    border-top: 1px solid #f0f0f0;
}
@media (max-width: 600px) {
    .region-dialog-title {
        padding: 16px;
        font-size: 22px;
    }
    .region-dialog-search, .region-dialog-list {
        padding-left: 16px;
        padding-right: 16px;
    }
    .region-dialog-actions {
        padding: 16px 12px;
    }
    .region-selection-count {
        display: none;
    }
    .region-dialog-actions ::v-deep .v-btn.v-btn {
        min-width: 0;
        font-size: 13px;
        letter-spacing: normal;
    }
}
</style>

<style>
.v-dialog.region-dialog {
    align-self: flex-start;
    margin: 48px 24px;
    max-height: calc(100% - 96px);
    border-radius: 20px;
    overflow: hidden;
}
@media (max-width: 600px) {
    .v-dialog.region-dialog {
        margin-top: 24px;
        margin-bottom: 24px;
        max-height: calc(100% - 48px);
    }
}
</style>
